import { apiClient } from '../utils/api-client';
import type { Crag } from '../types/crag';

export interface CragProfileResponse {
  data: {
    id: string;
    type: 'crag';
    attributes: Crag;
  };
}

/** One choice in the crag info form's grading-system pickers. */
export interface CragGradingSystemOption {
  id: string;
  name: string;
  code: string;
  climb_type: string;
}

export interface CragGradingSystems {
  boulder: CragGradingSystemOption[];
  route: CragGradingSystemOption[];
}

/**
 * What a crag admin may change from the panel — the Avo resource's fields minus
 * the ones that are not theirs (`code`, the scraper). A blank grading-system id
 * means "use the global default".
 */
export interface UpdateCragData {
  name?: string;
  bio?: string;
  website_url?: string;
  address?: string;
  city?: string;
  zipcode?: string;
  country?: string;
  state?: string;
  latitude?: number | null;
  longitude?: number | null;
  boulder_grading_system_id?: string | null;
  route_grading_system_id?: string | null;
  /** Both languages travel together — one person writes them in one form. */
  description_en?: string | null;
  description_it?: string | null;
  parking_latitude?: number | null;
  parking_longitude?: number | null;
  /** A freshly direct-uploaded .gpx, by signed id. */
  approach_gpx_signed_id?: string;
  /** Drops the track that is there. Ignored when a new one is being sent. */
  remove_approach_gpx?: boolean;
  /** A freshly direct-uploaded .glb, by signed id. */
  model_3d_signed_id?: string;
  /** The file is a crop of the model it replaces, so routes drawn on it stay put. */
  keep_model_3d_frame?: boolean;
  /** Drops the 3D model that is there. Ignored when a new one is being sent. */
  remove_model_3d?: boolean;
}

class CragsService {
  async getCragBySlug(slug: string): Promise<Crag> {
    const response = await apiClient.get(`/api/crags/${slug}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Crag not found');
      }
      throw new Error('Failed to fetch crag profile');
    }

    const data: CragProfileResponse = await response.json();
    return data.data.attributes;
  }

  async followCrag(cragSlug: string): Promise<void> {
    const response = await apiClient.post(`/api/crags/${cragSlug}/follow`, {});

    if (!response.ok) {
      throw new Error('Failed to follow crag');
    }
  }

  async unfollowCrag(cragSlug: string): Promise<void> {
    const response = await apiClient.delete(`/api/crags/${cragSlug}/unfollow`);

    if (!response.ok) {
      throw new Error('Failed to unfollow crag');
    }
  }

  /** The grading systems this crag may pick from. Crag admins only. */
  async getGradingSystems(cragSlug: string): Promise<CragGradingSystems> {
    const response = await apiClient.get(`/api/crags/${cragSlug}/grading_systems`);

    if (!response.ok) {
      throw new Error('Failed to fetch grading systems');
    }

    const data: { data: CragGradingSystems } = await response.json();
    return data.data;
  }

  async updateCrag(cragSlug: string, data: UpdateCragData): Promise<Crag> {
    const response = await apiClient.patch(`/api/crags/${cragSlug}`, {
      data: {
        attributes: data
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.errors?.[0]?.detail || 'Failed to update crag';
      throw new Error(errorMessage);
    }

    const responseData: CragProfileResponse = await response.json();
    return responseData.data.attributes;
  }

}

export const cragsService = new CragsService();
